<template>
  <div class="min-h-screen bg-gray-50 pb-24">
    <!-- 顶部返回栏 -->
    <div class="sticky top-0 z-10 bg-white border-b border-gray-200 px-4 py-3 flex items-center gap-3">
      <button @click="goBack" class="text-gray-600 text-sm flex items-center">
        <i class="fa fa-arrow-left mr-1"></i> 返回
      </button>
      <div class="flex-1 text-center text-gray-800 font-medium">服务单详情</div>
      <button v-if="!isEditing" @click="onEdit" class="text-blue-600 text-sm">编辑</button>
      <button v-else @click="onCancelEdit" class="text-gray-500 text-sm">取消</button>
    </div>

    <!-- 创建时间 -->
    <div class="px-3 mt-3">
      <div class="text-xs text-gray-500 text-center">
        创建时间：{{ ticket.created_at ? new Date(ticket.created_at).toLocaleString('zh-CN', { timeZone: 'Asia/Shanghai' }) : '-' }}
      </div>
    </div>

    <!-- 分组折叠 -->
    <div class="px-3">
      <!-- 基本信息 -->
      <div class="card">
        <div class="card-header" @click="toggleSection('basic')">
          <span class="card-title"><span class="bar"></span>基本信息</span>
          <i class="fa" :class="sections.basic ? 'fa-angle-up' : 'fa-angle-down'"></i>
        </div>
        <div class="card-body" v-show="sections.basic">
          <div class="field-row">
            <label class="field-label-inline">陪诊渠道</label>
            <select
              v-if="isEditing"
              v-model="ticket.channel"
              class="field-input-inline"
            >
              <option value="default">默认渠道</option>
              <option value="fushoukang">福寿康</option>
            </select>
            <div v-else class="field-value-inline">{{ channelLabel(ticket.channel) }}</div>
          </div>
          <div class="field-row" v-for="f in basicFields" :key="f.key">
            <label class="field-label-inline">{{ f.label }}</label>
            <input
              v-if="isEditing"
              v-model="ticket[f.key]"
              class="field-input-inline"
              :placeholder="`请输入${f.label}`"
            />
            <div v-else class="field-value-inline">{{ ticket[f.key] || '-' }}</div>
          </div>
        </div>
      </div>

      <!-- 时间记录 -->
      <div class="card">
        <div class="card-header" @click="toggleSection('time')">
          <span class="card-title"><span class="bar"></span>时间记录</span>
          <i class="fa" :class="sections.time ? 'fa-angle-up' : 'fa-angle-down'"></i>
        </div>
        <div class="card-body" v-show="sections.time">
          <div class="field-row" v-for="field in timeFields" :key="field">
            <label class="field-label-inline">{{ getFieldLabel(field) }}</label>
            <input
              v-if="isEditing"
              :value="ticket[field]"
              readonly
              @click="openTimePicker(field)"
              class="field-input-inline"
              :placeholder="`请选择${getFieldLabel(field)}`"
            />
            <div v-else class="field-value-inline">{{ ticket[field] || '-' }}</div>
          </div>
        </div>
      </div>

      <!-- 检查与住院 -->
      <div class="card">
        <div class="card-header" @click="toggleSection('check')">
          <span class="card-title"><span class="bar"></span>检查与住院</span>
          <i class="fa" :class="sections.check ? 'fa-angle-up' : 'fa-angle-down'"></i>
        </div>
        <div class="card-body" v-show="sections.check">
          <div class="field-row" v-for="f in checkFields" :key="f.key">
            <label class="field-label-inline">{{ f.label }}</label>
            <input
              v-if="isEditing"
              v-model="ticket[f.key]"
              class="field-input-inline"
              :placeholder="`请输入${f.label}`"
            />
            <div v-else class="field-value-inline">{{ ticket[f.key] || '-' }}</div>
          </div>
        </div>
      </div>

      <!-- 评价 -->
      <div class="card">
        <div class="card-header" @click="toggleSection('eval')">
          <span class="card-title"><span class="bar"></span>评价</span>
          <i class="fa" :class="sections.eval ? 'fa-angle-up' : 'fa-angle-down'"></i>
        </div>
        <div class="card-body" v-show="sections.eval">
          <div class="field-row eval-row" v-for="f in evalFields" :key="f.scoreKey">
            <label class="field-label-inline">{{ f.label }}</label>
            <!-- 编辑：星级选择 -->
            <template v-if="isEditing">
              <div class="stars">
                <span v-for="n in 5" :key="n"
                      class="star"
                      :class="{ active: Number(ticket[f.scoreKey]) >= n }"
                      @click="ticket[f.scoreKey] = String(n)">{{ Number(ticket[f.scoreKey]) >= n ? '★' : '☆' }}</span>
              </div>
              <span class="star-score">{{ ticket[f.scoreKey] ? ticket[f.scoreKey] + ' 星' : '未评分' }}</span>
              <textarea v-if="needExplain(f.scoreKey)"
                        v-model="ticket[f.reasonKey]"
                        class="field-textarea eval-text"
                        :placeholder="`评分${ticket[f.scoreKey]}星，请说明原因（不少于10字）`" />
            </template>
            <!-- 查看：展示星级 + 原因 -->
            <template v-else>
              <div class="stars">
                <span v-for="n in 5" :key="n"
                      class="star"
                      :class="{ active: Number(ticket[f.scoreKey]) >= n }">{{ Number(ticket[f.scoreKey]) >= n ? '★' : '☆' }}</span>
              </div>
              <span class="star-score">{{ ticket[f.scoreKey] ? ticket[f.scoreKey] + ' 星' : '未评分' }}</span>
              <div v-if="ticket[f.reasonKey]" class="eval-reason">原因：{{ ticket[f.reasonKey] }}</div>
            </template>
          </div>
        </div>
      </div>

      <!-- 陪诊小结 -->
      <div class="card">
        <div class="card-header" @click="toggleSection('summary')">
          <span class="card-title"><span class="bar"></span>陪诊小结</span>
          <i class="fa" :class="sections.summary ? 'fa-angle-up' : 'fa-angle-down'"></i>
        </div>
        <div class="card-body" v-show="sections.summary">
          <textarea
            v-if="isEditing"
            v-model="ticket.remarks"
            class="field-textarea"
            placeholder="请输入备注（陪诊小结）"
          ></textarea>
          <div v-else class="field-value-inline">{{ ticket.remarks || '-' }}</div>
        </div>
      </div>

      <!-- 上传图片 -->
      <div class="card">
        <div class="card-header" @click="toggleSection('upload')">
          <span class="card-title"><span class="bar"></span>上传图片</span>
          <i class="fa" :class="sections.upload ? 'fa-angle-up' : 'fa-angle-down'"></i>
        </div>
        <div class="card-body" v-show="sections.upload">
          <div class="upload-item" v-for="up in imageFieldConfigs" :key="up.key">
            <div class="upload-label-row">{{ up.label }}</div>
            <template v-if="isEditing">
              <van-uploader
                v-model="ticket[up.key]"
                :max-count="up.max"
                :multiple="up.max > 1"
                :before-read="uploadBeforeRead"
                :after-read="uploadAfterRead"
              />
            </template>
            <template v-else>
              <div class="image-grid" v-if="ticket[up.key] && ticket[up.key].length > 0">
                <img
                  v-for="(image, index) in ticket[up.key]"
                  :key="index"
                  :src="typeof image === 'string' ? image : image.url"
                  alt="图片"
                  class="preview-img"
                  @click="previewImage(typeof image === 'string' ? image : image.url)"
                />
              </div>
              <div v-else class="empty-text">无图片</div>
            </template>
          </div>
        </div>
      </div>
    </div>

    <!-- 底部操作栏（仅编辑模式显示） -->
    <div v-if="isEditing" class="fixed bottom-0 left-0 right-0 p-3 bottom-bar z-10">
      <button @click="onSave" class="btn btn-save w-full" :disabled="submitting">保存修改</button>
    </div>

    <!-- 时间选择器 -->
    <div v-if="timePickerShow" class="w-full fixed bottom-0 left-0">
      <div class="fixed inset-0 z-10 bg-#00000020" @click="onTimeCancel" />
      <van-datetime-picker
        v-model="tempTime"
        type="datetime"
        class="z-11 relative"
        :title="getFieldLabel(activeTimeField)"
        :min-date="minDate"
        @confirm="onTimeConfirm"
        @cancel="onTimeCancel"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { onMounted, ref, reactive } from 'vue'
import Compressor from 'compressorjs'
import axios from 'axios'
import { api } from '@/api'
import { useToastStore, useLoadingStore } from '@/stores'
import { $formatErrorMessage, $formatDate } from '@/helpers'
import { useRoute, useRouter } from 'vue-router/composables'

const { $toast } = useToastStore()
const { $showLoading, $hideLoading } = useLoadingStore()
const route = useRoute()
const router = useRouter()

// 查看模式 / 编辑模式
const isEditing = ref(false)
let snapshot: string = ''

// 分组折叠状态（详情页默认全展开）
const sections = reactive({
  basic: true,
  time: true,
  check: true,
  eval: true,
  summary: true,
  upload: true,
})
function toggleSection(key: keyof typeof sections) {
  sections[key] = !sections[key]
}

// 基本信息字段配置
const basicFields = [
  { key: 'customer_name', label: '客户姓名' },
  { key: 'visit_hospital', label: '就诊医院' },
  { key: 'visit_deparment', label: '就诊科室' },
  { key: 'visit_doctor', label: '专家名称' },
  { key: 'level', label: '级别' },
  { key: 'register_fee', label: '挂号费' },
  { key: 'patient_disease', label: '患者病情' },
  { key: 'expert_diagnosis', label: '专家诊断' },
  { key: 'medical_name', label: '药品名称' },
  { key: 'take_medical_time', label: '取药时间' },
  { key: 'expert_suggest', label: '专家建议' },
]
const checkFields = [
  { key: 'check_project', label: '检查项目' },
  { key: 'check_time_slot', label: '检查时间段' },
  { key: 'is_hospital_cert', label: '是否开住院证' },
  { key: 'is_hospital', label: '是否协调住院' },
]
const evalFields = [
  { scoreKey: 'doctor_attitude_score', reasonKey: 'doctor_attitude_reson', label: '医生态度' },
  { scoreKey: 'doctor_ability_score', reasonKey: 'doctor_ability_reson', label: '医生能力' },
  { scoreKey: 'hospital_env_score', reasonKey: 'hospital_env_reson', label: '医院环境' },
]
function needExplain(scoreField: string): boolean {
  const n = Number(ticket.value[scoreField])
  return n > 0 && n <= 3
}

// 时间字段列表
const timeFields = [
  'appointment_time', 'escort_reach_hospital_time', 'cusotomer_reach_hospital_time',
  'take_number_time', 'in_clinic_time', 'out_clinic_time',
  'check_appointment_time', 'service_end_time',
]

// 图片字段配置
const imageFieldConfigs = [
  { key: 'reach_proof_images', label: '到院打卡照片', max: 1 },
  { key: 'leave_proof_images', label: '离院打卡照片', max: 1 },
  { key: 'register_images', label: '挂号条', max: 1 },
  { key: 'check_images', label: '调查表-满意度调查表', max: 1 },
  { key: 'other_images', label: '其他', max: 5 },
] as const
const imageFields = imageFieldConfigs.map(i => i.key)

// ticket 数据
const ticket = ref<Record<string, any>>({
  id: 0,
  channel: '',
  customer_name: '', visit_hospital: '', visit_deparment: '', visit_doctor: '',
  level: '', register_fee: '', patient_disease: '', expert_diagnosis: '',
  medical_name: '', take_medical_time: '', expert_suggest: '',
  appointment_time: '', escort_reach_hospital_time: '', cusotomer_reach_hospital_time: '',
  take_number_time: '', in_clinic_time: '', out_clinic_time: '',
  check_project: '', check_time_slot: '', check_appointment_time: '',
  is_hospital_cert: '', is_hospital: '', service_end_time: '',
  remarks: '', doctor_attitude_score: '', doctor_ability_score: '', hospital_env_score: '',
  doctor_attitude_reson: '', doctor_ability_reson: '', hospital_env_reson: '',
  created_at: '',
  reach_proof_images: [], leave_proof_images: [], register_images: [],
  check_images: [], other_images: [],
})

const submitting = ref(false)

// 时间选择器
const timePickerShow = ref(false)
const activeTimeField = ref('')
const tempTime = ref<Date>(new Date())
const minDate = new Date(2020, 0, 1)

function openTimePicker(field: string) {
  activeTimeField.value = field
  const current = ticket.value[field] as string
  if (current) {
    tempTime.value = new Date(current.replace(/-/g, '/'))
    if (isNaN(tempTime.value.getTime())) tempTime.value = new Date()
  } else {
    tempTime.value = new Date()
  }
  timePickerShow.value = true
}
function onTimeConfirm(val: Date) {
  if (activeTimeField.value) {
    ticket.value[activeTimeField.value] = $formatDate(val)
  }
  timePickerShow.value = false
}
function onTimeCancel() {
  timePickerShow.value = false
}

function getFieldLabel(field: string) {
  switch (field) {
    case 'appointment_time': return '约定见面时间'
    case 'escort_reach_hospital_time': return '陪诊到院时间'
    case 'cusotomer_reach_hospital_time': return '客户到院时间'
    case 'take_number_time': return '取号时间'
    case 'in_clinic_time': return '进入诊室时间'
    case 'out_clinic_time': return '出诊室时间'
    case 'check_appointment_time': return '检查预约时间'
    case 'service_end_time': return '服务结束时间'
    default: return field
  }
}

function channelLabel(value: string) {
  switch (value) {
    case 'default': return '默认渠道'
    case 'fushoukang': return '福寿康'
    default: return value || '-'
  }
}

function onEdit() {
  snapshot = JSON.stringify(ticket.value)
  isEditing.value = true
}
function onCancelEdit() {
  ticket.value = JSON.parse(snapshot)
  isEditing.value = false
}

async function onSave() {
  if (submitting.value) return
  submitting.value = true
  try {
    $showLoading()
    const payload: any = { id: ticket.value.id }
    const textKeys = [
      'customer_name', 'visit_hospital', 'visit_deparment', 'visit_doctor',
      'level', 'register_fee', 'patient_disease', 'expert_diagnosis',
      'medical_name', 'take_medical_time', 'expert_suggest',
      'appointment_time', 'escort_reach_hospital_time',
      'cusotomer_reach_hospital_time', 'take_number_time',
      'in_clinic_time', 'out_clinic_time', 'check_project',
      'check_time_slot', 'check_appointment_time', 'service_end_time',
      'remarks', 'is_hospital_cert', 'is_hospital',
      'doctor_attitude_score', 'doctor_ability_score', 'hospital_env_score',
      'doctor_attitude_reson', 'doctor_ability_reson', 'hospital_env_reson',
      'channel',
    ]
    textKeys.forEach((k) => { payload[k] = ticket.value[k] ?? '' })
    imageFields.forEach((k) => {
      const arr = ticket.value[k] || []
      payload[k] = arr.map((item: any) => (typeof item === 'string' ? item : item.url)).filter(Boolean)
    })
    await api.post('/escorth5/serv_order/update_order', payload)
    $toast('保存成功')
    isEditing.value = false
    await fetchRecordDetail()
  } catch (error) {
    $toast($formatErrorMessage(error))
  } finally {
    submitting.value = false
    $hideLoading()
  }
}

function goBack() { router.go(-1) }
function previewImage(url: string) { window.open(url, '_blank') }

function uploadBeforeRead(file: File) {
  if (Array.isArray(file)) {
    return Promise.all(file.map(item => new Promise(resolve => new Compressor(item, { success: resolve, error: (e: any) => console.error(e.message) }))))
  }
  return new Promise(resolve => new Compressor(file, { success: resolve, error: (e: any) => console.error(e.message) }))
}
function getExtension(filename: string) { return filename.split('.').pop() || '' }
async function uploadAfterRead(file: { file: File; [key: string]: any }) {
  const files = Array.isArray(file) ? file : [file]
  for (const f of files) {
    f.status = 'uploading'
    f.message = '上传中...'
    try {
      const { data: signature } = await api.get(`/escorth5/serv_order/get_upload_params?ext=${getExtension(f.file.name)}`)
      const { status } = await axios.post(signature.host, {
        key: signature.key, policy: signature.policy, OSSAccessKeyId: signature.accessKeyId,
        signature: signature.signature, file: f.file,
      }, { headers: { 'Content-Type': 'multipart/form-data' } })
      if (status !== 204) { f.status = 'failed'; f.message = '上传失败'; return }
      f.status = 'done'
      f.url = signature.url
    } catch (error) {
      console.error(error)
      f.status = 'failed'
      f.message = '上传失败'
    }
  }
}

const fetchRecordDetail = async () => {
  try {
    const { data } = await api.get('/escorth5/serv_order/record_detail', {
      params: { id: route.params.id },
    })
    imageFields.forEach((key) => {
      if (typeof data[key] === 'string' && data[key]) {
        try {
          const parsed = JSON.parse(data[key])
          data[key] = (Array.isArray(parsed) ? parsed : []).map((url: any) =>
            typeof url === 'string' ? { url, status: 'done', isImage: true } : url)
        } catch { data[key] = [] }
      } else if (Array.isArray(data[key])) {
        data[key] = data[key].map((url: any) =>
          typeof url === 'string' ? { url, status: 'done', isImage: true } : url)
      } else {
        data[key] = []
      }
    })
    ticket.value = { ...ticket.value, ...data }
  } catch (error) {
    $toast($formatErrorMessage(error))
  }
}

onMounted(() => { fetchRecordDetail() })
</script>

<style scoped>
/* ===== 卡片分组 ===== */
.card {
  background: #fff;
  border-radius: 12px;
  margin: 10px 0;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0,0,0,.04);
}
.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  cursor: pointer;
  user-select: none;
}
.card-title {
  font-size: 15px;
  font-weight: 600;
  color: #1e293b;
  display: flex;
  align-items: center;
}
.card-title .bar {
  display: inline-block;
  width: 3px;
  height: 14px;
  background: #3b82f6;
  border-radius: 2px;
  margin-right: 8px;
}
.card-header i {
  color: #94a3b8;
  font-size: 18px;
}
.card-body {
  padding: 4px 16px 14px;
}

/* ===== 字段行 ===== */
.field-row {
  display: flex;
  align-items: flex-start;
  padding: 10px 0;
  border-bottom: 1px solid #f1f5f9;
  gap: 10px;
}
.field-row:last-child { border-bottom: none; }
.field-label-inline {
  flex-shrink: 0;
  width: 90px;
  font-size: 14px;
  color: #475569;
  padding-top: 12px;
  line-height: 1.4;
}
.field-value-inline {
  flex: 1;
  min-width: 0;
  font-size: 15px;
  color: #1e293b;
  line-height: 1.6;
  word-break: break-all;
  padding-top: 11px;
}
.field-input-inline {
  flex: 1;
  min-width: 0;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 10px 12px;
  font-size: 15px;
  background: #fff;
  box-sizing: border-box;
  min-height: 42px;
  transition: border-color .2s;
}
.field-input-inline:focus {
  outline: none;
  border-color: #3b82f6;
}
.field-input-inline:read-only {
  background: #f8fafc;
  color: #475569;
  cursor: pointer;
}
.field-input-inline::placeholder {
  color: #cbd5e1;
  font-size: 14px;
}
.field-textarea {
  width: 100%;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 10px 12px;
  font-size: 15px;
  background: #fff;
  box-sizing: border-box;
  min-height: 100px;
  resize: vertical;
  line-height: 1.6;
}
.field-textarea:focus {
  outline: none;
  border-color: #3b82f6;
}

/* ===== 上传 ===== */
.upload-item {
  padding: 10px 0;
  border-bottom: 1px solid #f1f5f9;
}
.upload-item:last-child { border-bottom: none; }
.upload-label-row {
  font-size: 14px;
  color: #475569;
  margin-bottom: 8px;
}
.image-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(80px, 1fr));
  gap: 8px;
}
.preview-img {
  width: 100%;
  height: 80px;
  object-fit: cover;
  border-radius: 6px;
  cursor: pointer;
  transition: transform .2s;
}
.preview-img:active { transform: scale(.95); }
.empty-text {
  color: #94a3b8;
  font-size: 13px;
  text-align: center;
  padding: 16px 0;
}

/* ===== 底部按钮 ===== */
.bottom-bar {
  background: #fff;
  border-top: 1px solid #e2e8f0;
  box-shadow: 0 -2px 12px rgba(0,0,0,.06);
}
.btn {
  border: none;
  border-radius: 10px;
  padding: 12px 16px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all .2s;
}
.btn:disabled { opacity: .6; }
.btn-save {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: #fff;
}
.btn-save:active { background: #1d4ed8; }

/* ===== 评价星级 ===== */
.eval-row {
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
}
.eval-row .field-label-inline {
  width: auto;
  padding-top: 0;
  margin-bottom: 4px;
}
.stars {
  display: flex;
  gap: 6px;
  font-size: 28px;
  line-height: 1;
}
.star {
  color: #cbd5e1;
  user-select: none;
}
.star.active { color: #f59e0b; }
.star-score {
  color: #64748b;
  font-size: 13px;
  margin-left: 2px;
}
.eval-text {
  margin-top: 4px;
  min-height: 70px;
}
.eval-reason {
  font-size: 13px;
  color: #64748b;
  margin-top: 2px;
  line-height: 1.5;
}
</style>
